import React, {useState, useEffect} from 'react';
import {
    Settings,
    UserPlus,
    Lock,
    Eye,
    EyeOff,
    AlertTriangle,
    Loader2,
    CheckCircle,
    BookOpen,
    Power,
    Save
} from 'lucide-react';

// Import components
import ToggleSignup from '../Components/ToggleSignup';
import AddBook from '../Components/AddBook';

const SettingsPage = () => {
    // New user form state
    const [newUserData, setNewUserData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'user',
        sect: 'Hanafi'
    });
    const [isUserLoading, setIsUserLoading] = useState(false);
    const [userError, setUserError] = useState(null);
    const [userSuccess, setUserSuccess] = useState(false);

    // Password update form state
    const [passwordData, setPasswordData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [passwordShown, setPasswordShown] = useState(false);
    const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [isPasswordLoading, setIsPasswordLoading] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState(false);

    // Handle new user input changes
    const handleNewUserInputChange = (event) => {
        const {name, value} = event.target;
        setNewUserData({
            ...newUserData,
            [name]: value
        });
    };

    // Handle password update input changes
    const handlePasswordInputChange = (event) => {
        const {name, value} = event.target;
        setPasswordData({
            ...passwordData,
            [name]: value
        });
    };

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setPasswordShown(!passwordShown);
    };

    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordShown(!confirmPasswordShown);
    };

    // Create new user
    const createNewUser = async (event) => {
        event.preventDefault();
        setIsUserLoading(true);
        setUserError(null);
        setUserSuccess(false);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch('https://api.digitalaalim.com/admin/create-user/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify(newUserData)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Failed to create user');
            }

            // Clear form and show success message
            setNewUserData({
                name: '',
                email: '',
                password: '',
                role: 'user',
                sect: 'Hanafi'
            });

            setUserSuccess(true);

            // Hide success message after 3 seconds
            setTimeout(() => {
                setUserSuccess(false);
            }, 3000);
        } catch (error) {
            setUserError(error.message);
        } finally {
            setIsUserLoading(false);
        }
    };

    // Update password
    const updatePassword = async (event) => {
        event.preventDefault();

        // Validate passwords match
        if (passwordData.password !== passwordData.confirmPassword) {
            setPasswordError('Passwords do not match!');
            return;
        }

        setIsPasswordLoading(true);
        setPasswordError('');
        setPasswordSuccess(false);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch('https://api.digitalaalim.com/user/update', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    name: passwordData.name,
                    email: passwordData.email,
                    password: passwordData.password
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Failed to update password');
            }

            // Clear form and show success message
            setPasswordData({
                name: '',
                email: '',
                password: '',
                confirmPassword: ''
            });

            setPasswordSuccess(true);

            // Hide success message after 3 seconds
            setTimeout(() => {
                setPasswordSuccess(false);
            }, 3000);
        } catch (error) {
            setPasswordError(error.message);
        } finally {
            setIsPasswordLoading(false);
        }
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <Settings className="text-blue-600" size={32}/>
                    Settings
                </h1>
                <p className="mt-2 text-gray-600">
                    Manage system settings, users, and application preferences
                </p>
            </div>

            {/* Settings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Add User Card */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                            <UserPlus className="text-blue-600" size={20}/>
                            Add New User
                        </h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Create a new user account with specific role and permissions
                        </p>
                    </div>

                    <form onSubmit={createNewUser} className="p-6 space-y-4">
                        {/* Success message */}
                        {userSuccess && (
                            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mb-4">
                                <div className="flex items-center">
                                    <CheckCircle size={20} className="text-green-500 mr-2 flex-shrink-0"/>
                                    <p className="text-green-700">User created successfully!</p>
                                </div>
                            </div>
                        )}

                        {/* Error message */}
                        {userError && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded mb-4">
                                <div className="flex items-center">
                                    <AlertTriangle size={20} className="text-red-500 mr-2 flex-shrink-0"/>
                                    <p className="text-red-700">{userError}</p>
                                </div>
                            </div>
                        )}

                        {/* Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                Full Name
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={newUserData.name}
                                onChange={handleNewUserInputChange}
                                required
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="John Doe"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={newUserData.email}
                                onChange={handleNewUserInputChange}
                                required
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="john@example.com"
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                Password
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={newUserData.password}
                                onChange={handleNewUserInputChange}
                                required
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="••••••••"
                            />
                        </div>

                        {/* Role */}
                        <div>
                            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                                Role
                            </label>
                            <select
                                id="role"
                                name="role"
                                value={newUserData.role}
                                onChange={handleNewUserInputChange}
                                required
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="madrasah-admin">Madrasah Admin</option>
                            </select>
                        </div>

                        {/* Sect */}
                        <div>
                            <label htmlFor="sect" className="block text-sm font-medium text-gray-700 mb-1">
                                Sect
                            </label>
                            <select
                                id="sect"
                                name="sect"
                                value={newUserData.sect}
                                onChange={handleNewUserInputChange}
                                required
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                                <option value="Hanafi">Hanafi</option>
                                <option value="Jafari">Jafari</option>
                                <option value="Hanbali">Hanbali</option>
                                <option value="Shafi">Shafi</option>
                                <option value="Malaki">Malaki</option>
                                <option value="Salafi">Salafi</option>
                            </select>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isUserLoading}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isUserLoading ? (
                                <>
                                    <Loader2 size={16} className="animate-spin mr-2"/>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <UserPlus size={16} className="mr-2"/>
                                    Create User
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Update Password Card */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                            <Lock className="text-blue-600" size={20}/>
                            Update Password
                        </h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Change password for any user in the system
                        </p>
                    </div>

                    <form onSubmit={updatePassword} className="p-6 space-y-4">
                        {/* Success message */}
                        {passwordSuccess && (
                            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mb-4">
                                <div className="flex items-center">
                                    <CheckCircle size={20} className="text-green-500 mr-2 flex-shrink-0"/>
                                    <p className="text-green-700">Password updated successfully!</p>
                                </div>
                            </div>
                        )}

                        {/* Error message */}
                        {passwordError && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded mb-4">
                                <div className="flex items-center">
                                    <AlertTriangle size={20} className="text-red-500 mr-2 flex-shrink-0"/>
                                    <p className="text-red-700">{passwordError}</p>
                                </div>
                            </div>
                        )}

                        {/* Name */}
                        <div>
                            <label htmlFor="pwd-name" className="block text-sm font-medium text-gray-700 mb-1">
                                Full Name
                            </label>
                            <input
                                type="text"
                                id="pwd-name"
                                name="name"
                                value={passwordData.name}
                                onChange={handlePasswordInputChange}
                                required
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="John Doe"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label htmlFor="pwd-email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                            </label>
                            <input
                                type="email"
                                id="pwd-email"
                                name="email"
                                value={passwordData.email}
                                onChange={handlePasswordInputChange}
                                required
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="john@example.com"
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="pwd-password" className="block text-sm font-medium text-gray-700 mb-1">
                                New Password
                            </label>
                            <div className="relative">
                                <input
                                    type={passwordShown ? 'text' : 'password'}
                                    id="pwd-password"
                                    name="password"
                                    value={passwordData.password}
                                    onChange={handlePasswordInputChange}
                                    required
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-10"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                                    onClick={togglePasswordVisibility}
                                >
                                    {passwordShown ? (
                                        <EyeOff size={16}/>
                                    ) : (
                                        <Eye size={16}/>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="pwd-confirm" className="block text-sm font-medium text-gray-700 mb-1">
                                Confirm Password
                            </label>
                            <div className="relative">
                                <input
                                    type={confirmPasswordShown ? 'text' : 'password'}
                                    id="pwd-confirm"
                                    name="confirmPassword"
                                    value={passwordData.confirmPassword}
                                    onChange={handlePasswordInputChange}
                                    required
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-10"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                                    onClick={toggleConfirmPasswordVisibility}
                                >
                                    {confirmPasswordShown ? (
                                        <EyeOff size={16}/>
                                    ) : (
                                        <Eye size={16}/>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isPasswordLoading}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isPasswordLoading ? (
                                <>
                                    <Loader2 size={16} className="animate-spin mr-2"/>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <Save size={16} className="mr-2"/>
                                    Update Password
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Toggle Signup Card */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                            <Power className="text-blue-600" size={20}/>
                            Registration Settings
                        </h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Control user registration and signup features
                        </p>
                    </div>

                    <div className="p-6">
                        <ToggleSignup/>
                    </div>
                </div>

                {/* Add Book Card */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                            <BookOpen className="text-blue-600" size={20}/>
                            Add New Book
                        </h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Add new book details to the system catalog
                        </p>
                    </div>

                    <div className="p-6">
                        <AddBook/>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;